import { listDocName, type ListItem } from '@awwyeah/shared';
import { Plus, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useYArray, useYDoc, useYMap } from '../../sync';
import { useUser } from '../auth';
import { Fab } from '../../components/Fab';
import { ListItemRow } from './components/ListItemRow';
import {
  addItem,
  buildSectionGroups,
  splitListInput,
  updateItem,
  deleteItem,
  splitCheckedUnchecked,
  toggleItem,
} from './listOps';
import { useHouseholdRoster } from './useHouseholdRoster';
import { useRemoteChangeFlash } from './useRemoteChangeFlash';
import { useListMeta } from './useListRegistries';

/** Best-effort Screen Wake Lock — feature-detected, no dependency (navigator.wakeLock). */
function useWakeLock(active: boolean): void {
  const sentinel = useRef<{ release: () => Promise<void> } | null>(null);

  useEffect(() => {
    if (!active) return undefined;
    const nav = navigator as Navigator & {
      wakeLock?: { request: (type: 'screen') => Promise<{ release: () => Promise<void> }> };
    };
    if (!nav.wakeLock) return undefined;

    let cancelled = false;
    const acquire = async (): Promise<void> => {
      try {
        const lock = await nav.wakeLock?.request('screen');
        if (lock && !cancelled) sentinel.current = lock;
        else await lock?.release();
      } catch {
        // Wake lock is best-effort — denied/unsupported is silently fine.
      }
    };
    void acquire();

    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') void acquire();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      void sentinel.current?.release();
      sentinel.current = null;
    };
  }, [active]);
}

/**
 * Full-screen shopping mode (ROADMAP §2 Phase 1 Lists item 4 — "the marquee screen").
 *
 * The router (WS-3's `router.tsx`) wraps every non-public module route in the app chrome
 * (bottom tabs, header) and that file is outside this module's directories. Rather than
 * touch it, this page covers the whole viewport itself with a `fixed inset-0` layer — the
 * chrome is still mounted behind it but visually gone, which is what the design means by
 * "no tab bar, no chrome, the list owns the glass" (see docs/DECISIONS.md).
 *
 * Because it opts out of the shell it must also do the shell's safe-area job itself: with
 * `viewport-fit=cover` and no insets, an installed iPhone PWA drew this header *under* the
 * status bar and both header buttons became untappable — "totally disabled by the iPhone"
 * (usability testing 2026-08-01). The header pads for `safe-area-inset-top`, the footer for
 * `safe-area-inset-bottom`.
 */
export function ShoppingModePage(): JSX.Element {
  const { listId } = useParams<{ listId: string }>();
  const { user } = useUser();
  const navigate = useNavigate();
  const { getMember, meId } = useHouseholdRoster();

  // WS-15: reads through the family + personal registry merge (see useListRegistries).
  const { meta: list } = useListMeta(listId);

  const doc = useYDoc(listId ? listDocName(listId) : listDocName('missing'));
  const { array: order } = useYArray<string>(doc, 'order');
  const { map, entries } = useYMap<ListItem>(doc, 'items');
  const items = entries.map(([, item]) => item);

  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  // Starts expanded (Dan: "start with the 'in the cart' portion open") — still collapsible.
  const [showCart, setShowCart] = useState(true);
  const flashing = useRemoteChangeFlash(items, meId);

  useWakeLock(Boolean(list));

  if (!user || !listId || !list) return <></>;

  const { unchecked, checked } = splitCheckedUnchecked(items);
  const groups = buildSectionGroups(unchecked);

  const submitAdd = (): void => {
    const text = draft.trim();
    if (text) addItem(doc, order, map, { text, userId: user.id });
    setDraft('');
  };

  return (
    <div className="fixed inset-0 z-30 flex flex-col bg-bg" data-testid="shopping-mode-page">
      <header className="flex min-h-[60px] flex-shrink-0 items-center gap-3 border-b-2 border-line-strong bg-surface px-3 pt-[env(safe-area-inset-top)]">
        <button
          type="button"
          aria-label="Exit shopping mode"
          data-testid="exit-shopping-mode"
          onClick={() => navigate(`/lists/${listId}`)}
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center"
        >
          <X size={22} />
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-lg font-bold tracking-tight text-fg">
            {list.emoji ? `${list.emoji} ` : ''}
            {list.name}
          </div>
          <div className="text-sm text-fg-3">
            {unchecked.length} to go · {checked.length} in the cart
          </div>
        </div>
        <button
          type="button"
          aria-label="Add item"
          data-testid="shopping-add-button"
          onClick={() => setAdding(true)}
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center"
        >
          <Plus size={22} />
        </button>
      </header>

      {adding && (
        <div className="flex flex-shrink-0 items-center gap-2 border-b border-line bg-surface px-3 py-2">
          <input
            data-testid="shopping-add-input"
            autoFocus
            className="h-11 min-w-0 flex-1 rounded border border-line bg-bg px-3 text-base text-fg"
            placeholder="Add mid-aisle…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                // Keep the input open and focused so items can be chained by keyboard.
                e.preventDefault();
                submitAdd();
              }
            }}
            onPaste={(e) => {
              const parts = splitListInput(e.clipboardData.getData('text'));
              if (parts.length > 1) {
                e.preventDefault();
                for (const part of parts) addItem(doc, order, map, { text: part, userId: user.id });
                setDraft('');
              }
            }}
            onBlur={() => {
              submitAdd();
              setAdding(false);
            }}
          />
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {unchecked.length === 0 && checked.length === 0 && (
          <div className="flex flex-col gap-2 p-6 text-center">
            <p className="text-lg font-bold text-fg">Nothing on this list yet</p>
            <p className="text-sm text-fg-3">Tap + to add the first thing.</p>
          </div>
        )}
        {unchecked.length === 0 && checked.length > 0 && (
          <div className="flex flex-col gap-2 p-6" data-testid="shopping-done-banner">
            <p className="text-lg font-bold text-fg">That's everything. Go home.</p>
            <p className="text-sm text-fg-2">
              {checked.length} items. The receipt's going to hurt.
            </p>
          </div>
        )}
        {groups.map((group) => (
          <div key={group.name}>
            <div className="flex h-[38px] items-center justify-between bg-bg px-4">
              <span className="text-sm font-bold uppercase tracking-wide text-fg-2">
                {group.name}
              </span>
              <span className="text-sm text-fg-3">{group.items.length}</span>
            </div>
            <div className="border-t border-line bg-surface">
              {group.items.map((item) => (
                <ListItemRow
                  key={item.id}
                  item={item}
                  variant="shopping"
                  getMember={getMember}
                  meId={meId}
                  flashing={flashing.has(item.id)}
                  onToggle={() => toggleItem(doc, map, item.id, user.id)}
                  onDelete={() => deleteItem(doc, order, map, item.id)}
                  onTextChange={(text) => updateItem(doc, map, item.id, user.id, { text })}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {checked.length > 0 && (
        <div className="flex-shrink-0" data-testid="shopping-cart-section">
          <button
            type="button"
            data-testid="cart-section-toggle"
            className="flex h-11 w-full items-center justify-between border-t-2 border-line-strong bg-bg px-4"
            onClick={() => setShowCart((v) => !v)}
          >
            <span className="text-sm font-bold uppercase tracking-wide text-fg-3">
              In the cart · {checked.length}
            </span>
            <span className="text-sm font-semibold text-fg-3">{showCart ? 'Hide' : 'Show'}</span>
          </button>
          {showCart && (
            <div className="max-h-[35vh] overflow-y-auto bg-bg">
              {checked.map((item) => (
                <ListItemRow
                  key={item.id}
                  item={item}
                  variant="shopping"
                  getMember={getMember}
                  meId={meId}
                  flashing={flashing.has(item.id)}
                  onToggle={() => toggleItem(doc, map, item.id, user.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <footer className="flex flex-shrink-0 items-center gap-3 border-t-2 border-line-strong bg-surface px-4 py-3 pb-[max(12px,env(safe-area-inset-bottom))]">
        <div className="flex-1">
          <div className="text-sm font-semibold text-fg">{unchecked.length} to go</div>
        </div>
        <button
          type="button"
          data-testid="finish-shopping"
          className="h-[52px] flex-shrink-0 rounded-md bg-inverse px-5 py-3 text-base font-semibold text-inverse-fg"
          onClick={() => navigate(`/lists/${listId}`)}
        >
          Done
        </button>
      </footer>

      {!adding && (
        <Fab
          label="Add item"
          testId="shopping-add-fab"
          placement="overlay"
          onClick={() => setAdding(true)}
        />
      )}
    </div>
  );
}
